// src/pages/admin/AdminLayout.jsx
import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";

export default function AdminLayout() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);

  // login form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authMsg, setAuthMsg] = useState("");
  const [authType, setAuthType] = useState(null); // "error" | "success" | null
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    // 讀取現有 session
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session || null);
      setChecking(false);
    });

    // 監聽登入/登出變化
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => {
      setSession(s);
      setChecking(false);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (e) => {
    e?.preventDefault();
    setAuthLoading(true);
    setAuthMsg("");
    setAuthType(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;
      setAuthMsg("✅ 登入成功！");
      setAuthType("success");
      // 可選：登入後回到 /admin 首頁
      navigate("/admin");
    } catch (err) {
      setAuthMsg(`登入失敗：${err?.message || "請確認帳密"}`);
      setAuthType("error");
    } finally {
      setAuthLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setEmail("");
    setPassword("");
    navigate("/admin");
  };

  // 未完成檢查時的簡單過場
  if (checking) {
    return (
      <section
        style={{ maxWidth: 960, margin: "24px auto", padding: "0 16px" }}
      >
        載入中…
      </section>
    );
  }

  // ❌ 未登入 → 顯示統一的 Email/Password 登入卡片（所有 /admin 子頁皆如此）
  if (!session) {
    return (
      <section
        style={{ maxWidth: 400, margin: "48px auto", padding: "0 16px" }}
      >
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "#fff",
            boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
            padding: 16,
          }}
        >
          <h2 style={{ margin: "0 0 12px", fontSize: 20, fontWeight: 800 }}>
            後台登入
          </h2>
          <form onSubmit={signIn} style={{ display: "grid", gap: 10 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>
                Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{
                  height: 40,
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  padding: "0 10px",
                }}
                required
              />
            </label>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>
                密碼
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  height: 40,
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  padding: "0 10px",
                }}
                required
              />
            </label>
            <button
              type="submit"
              disabled={authLoading}
              style={{
                height: 40,
                borderRadius: 10,
                border: "1px solid #0ea5e9",
                background: "#0ea5e9",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {authLoading ? "登入中…" : "登入"}
            </button>

            {authMsg && (
              <div
                style={{
                  marginTop: 6,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid",
                  ...(authType === "success"
                    ? {
                        background: "#ecfdf5",
                        borderColor: "#6ee7b7",
                        color: "#065f46",
                      }
                    : {
                        background: "#fef2f2",
                        borderColor: "#fecaca",
                        color: "#991b1b",
                      }),
                }}
              >
                {authMsg}
              </div>
            )}
          </form>
        </div>
      </section>
    );
  }

  // ✅ 已登入 → 顯示上方導覽 + 右上登出，並渲染子路由
  return (
    <section style={{ maxWidth: 1180, margin: "16px auto", padding: "0 16px" }}>
      {/* Top Nav */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          justifyContent: "space-between",
          padding: "10px 12px",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          background: "#fff",
          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <strong style={{ fontSize: 18 }}>管理後台</strong>
          <nav style={{ display: "flex", gap: 8 }}>
            <NavLink
              to="/admin"
              end
              style={({ isActive }) => ({
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid " + (isActive ? "#0ea5e9" : "#e5e7eb"),
                background: isActive ? "#e0f2fe" : "#fff",
                color: isActive ? "#0c4a6e" : "#374151",
                fontWeight: 600,
              })}
            >
              首頁
            </NavLink>
            <NavLink
              to="/admin/notice"
              style={({ isActive }) => ({
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid " + (isActive ? "#0ea5e9" : "#e5e7eb"),
                background: isActive ? "#e0f2fe" : "#fff",
                color: isActive ? "#0c4a6e" : "#374151",
                fontWeight: 600,
              })}
            >
              公告管理
            </NavLink>
            <NavLink
              to="/admin/schedule"
              style={({ isActive }) => ({
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid " + (isActive ? "#0ea5e9" : "#e5e7eb"),
                background: isActive ? "#e0f2fe" : "#fff",
                color: isActive ? "#0c4a6e" : "#374151",
                fontWeight: 600,
              })}
            >
              班表管理
            </NavLink>
            <NavLink
              to="/admin/slot"
              style={({ isActive }) => ({
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid " + (isActive ? "#0ea5e9" : "#e5e7eb"),
                background: isActive ? "#e0f2fe" : "#fff",
                color: isActive ? "#0c4a6e" : "#374151",
                fontWeight: 600,
              })}
            >
              門診預約
            </NavLink>

            <NavLink
              to="/admin/team"
              style={({ isActive }) => ({
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid " + (isActive ? "#0ea5e9" : "#e5e7eb"),
                background: isActive ? "#e0f2fe" : "#fff",
                color: isActive ? "#0c4a6e" : "#374151",
                fontWeight: 600,
              })}
            >
              醫師卡片
            </NavLink>
            <NavLink
              to="/admin/imageSlider"
              style={({ isActive }) => ({
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid " + (isActive ? "#0ea5e9" : "#e5e7eb"),
                background: isActive ? "#e0f2fe" : "#fff",
                color: isActive ? "#0c4a6e" : "#374151",
                fontWeight: 600,
              })}
            >
              書法管理
            </NavLink>
          </nav>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, color: "#6b7280" }}>
            {session?.user?.email}
          </span>
          <button
            onClick={signOut}
            style={{
              height: 32,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid #0a3a50",
              background: "#0f4d69",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
            }}
            title="登出"
          >
            登出
          </button>
        </div>
      </div>

      {/* 子頁渲染 */}
      <Outlet />
    </section>
  );
}
